import { ClientHttpRequester } from '@smarterservices/client-http-requester';
import { config } from '../config';
import { ParamsNotFoundError, ApiError } from '../helpers/definedErrors';
import {
  isValidISODate,
  isValidProctorUserSid,
  isValidSessionStatus,
  isValidDurationMinutes
} from './ApiClientValidation';

/**
 * @typedef {Object} ApiClientOptions
 * @property {string} [spToken] - The authentication token for the API
 * @property {string} [spMediaToken] - The media token for the API
 * @property {Object} [sessionPathParams] - Path parameters for the session
 */

class ApiClient {
  constructor(options) {
    if (options?.spToken) {
      this.spToken = options.spToken;
    }
    if (options?.spMediaToken) {
      this.spMediaToken = options.spMediaToken;
    }

    this.sessionPathParams = options?.sessionPathParams || {};

    const requesterConfig = {
      apiUrl: config.proctoring.api.baseUrl,
      retry: {
        enabled: config.proctoring.api.retry,
      },
    };

    this.requester = ClientHttpRequester(requesterConfig);

    const mediaApiRequesterConfig = {
      apiUrl: config.media.api.baseUrl,
      retry: {
        enabled: config.media.api.retry,
      },
    };

    this.mediaRequester = ClientHttpRequester(mediaApiRequesterConfig);
  }

  /**
   * Build the headers for an API request
   * @param {Object} headers - the base headers
   * @returns {Object} - the headers with the auth token included
   */
  buildHeaders(headers = {}) {
    const authHeader = { token: this.spToken };
    return { ...headers, ...authHeader };
  }

  /**
   * Replaces path params with given values
   * @param {string} path - the path with params
   * @param {Object} params - the values to replace the params
   * @returns {string} - the path with params replaced
   */
  buildPath(path, params) {
    return path.replace(/:([^/]+)/g, (_, key) => {
      return key in params ? params[key] : `:${key}`;
    });
  }

  async registerTrack(params) {
    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    const path =
      '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/tracks';
    const url = this.buildPath(path, pathReplacements);

    try {
      const response = await this.mediaRequester.put(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async startRecording(params = null) {
    if (!params) {
        params = JSON.parse(sessionStorage.getItem('spParams'));
        if (!params) {
          console.error('No params were found!');
          throw new ParamsNotFoundError();
        }
        try {
          const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/recording`;
          const headers = {
            token: params.token,
          };
          const payload = {
            action: 'start',
          };
          const response = await this.requester.put(url, headers, payload);
          return {
            success: true,
            data: response.data
          }
        } catch (error) {
          if(error?.response?.data && error?.response?.data?.code === '1571'){
            return {
              success: false,
              message: error?.response?.data?.message
            }
          } else {
            return {
              success: false,
              message: error?.message
            }
          }
        }
      }
  };

  async getRecording(params = null) {
    if (!params) {
      params = JSON.parse(sessionStorage.getItem('spParams'));
      if (!params) {
        console.error('No params were found!');
        throw new ParamsNotFoundError();
      }
    }
    try {
      const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/recording`;
      const headers = {
        token: params.token,
      };
      const response = await this.requester.get(url, headers);
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        message: error?.message
      }
    }
  };

  async checkRecordingFileAvailability(params) {
    const pathReplacements = {
      sessionSid: params.sessionSid,
      installSid: params.installSid,
      examSid: params.examSid,
      spMediaAuthToken: params.spMediaAuthToken,
    };

    // Endpoint should return information about the m3u8 file availability, we do not need track data at this point...
    const path =
      '/v1/:spMediaAuthToken/installs/:installSid/exams/:examSid/sessions/:sessionSid/metadata?includeTrackData=false';
    const url = this.buildPath(path, pathReplacements);

    try {
      const response = await this.mediaRequester.get(url);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  /**
   * Creates a new annotation for an exam session
   *
   * @async
   * @param {Object} [params] - Parameters for creating an annotation
   * @param {string} [params.installSid] - The installation SID
   * @param {string} [params.examSid] - The exam SID
   * @param {string} [params.sessionSid] - The session SID
   * @param {Object} params.payload - The annotation payload
   * @param {string} params.payload.typeId - Type identifier for the annotation
   * @param {string} [params.payload.details] - Optional details about the annotation
   * @param {string} params.payload.createdById - User who created the annotation (max length: 34)
   * @param {string} [params.payload.eventDate] - Optional date when the annotation was created (ISO date-time format)
   * @param {Object} [params.payload.metadata] - Optional metadata associated with the annotation
   * @returns {Promise<Object>} Object containing success status and response data or error information
   * @returns {boolean} returns.successful - Whether the API call was successful
   * @returns {Object} [returns.data] - The response data if successful
   * @returns {number} [returns.statusCode] - HTTP status code if request failed
   * @returns {Error} [returns.error] - Error object if request failed
   */
  async addAnnotation(params = {}) {

    params = {
      ...params,
      ...this.sessionPathParams
    };

    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    if (!params.payload.createdById) {
      params.payload.createdById = "System";
    }

    const url = this.buildPath(
      '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations',
      pathReplacements
    );

    try {
      const response = await this.requester.post(
        url,
        this.buildHeaders(),
        params.payload
      );
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, statusCode: e?.response?.status, error: e };
    }
  }

  async modifyJourneySteps(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }

    try {
      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        'v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/step',
        pathReplacements
      );

      const headers = this.buildHeaders();

      // Include the fields provided in the request
      const payload = {}
      if (params.steps && Array.isArray(params.steps)) {
        payload.steps = params.steps;
      }

      if (params.addedBy) {
        payload.addedBy = params.addedBy;
      } else {
        payload.addedBy = "Platform";
      }

      if (params.outcomeData) {
        payload.outcomeData = params.outcomeData;
      }

      payload.pushUserToNextStep = params.pushUserToNextStep ?? false;
      payload.completeCurrentStep = params.completeCurrentStep ?? false;

      const response = await this.requester.post(url, headers, payload);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  async getSession(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }
    try {
      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid',
        pathReplacements
      );

      const headers = this.buildHeaders();

      const response = await this.requester.get(url, headers);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  async getSessionMediaConnectionInfo(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }
    try {
      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/mediaConnection',
        pathReplacements
      );

      const headers = this.buildHeaders();

      const response = await this.requester.get(url, headers);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  async getSessionJourney(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }

    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    let url = this.buildPath(
      '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/journey',
      pathReplacements
    );

    // Add journeyType query parameter based on isReconnect flag
    const journeyType = params.isReconnect ? 'reconnect' : 'initial';
    url += `?journeyType=${journeyType}`;

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  }

  async getRecordingInfo(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }

    const pathReplacements = {
      installSid: params.installSid,
      examSid: params.examSid,
      sessionSid: params.sessionSid,
    };

    let url = this.buildPath(
      '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/recording',
      pathReplacements
    );

    try {
      const response = await this.requester.get(url, this.buildHeaders());
      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  }

  async getSessionStart(params = {}) {

    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }
    try {

      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/start',
        pathReplacements
      );

      const headers = this.buildHeaders();

      const response = await this.requester.get(url, headers);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  async endSession(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }

    try {
      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        'v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/end',
        pathReplacements
      );

      const headers = this.buildHeaders();

      // Include the fields provided in the request
      const payload = {}

      const allowedReasons = ['internet-disconnected', 'proctor-ended', 'test-taker-ended', 'virtual-agent-ended', 'extension-non-responsive'];
      if (params.reason && allowedReasons.includes(params.reason)) {
        payload.reason = params.reason;
      } else {
        payload.reason = 'test-taker-ended';
      }

      const response = await this.requester.post(url, headers, payload);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  /**
   * Updates a session with the provided parameters
   * @param {Object} params - Parameters for the session update
   * @param {string} params.installSid - Installation SID
   * @param {string} params.examSid - Exam SID
   * @param {string} params.sessionSid - Session SID
   * @param {string} [params.reviewByDate] - ISO date string for review by date (with Z suffix)
   * @param {string} [params.reviewByUserSid] - Proctor User SID who will review
   * @param {string} [params.reviewCompletedDate] - ISO date string for review completed date (with Z suffix)
   * @param {boolean} [params.reviewRequired] - Whether review is required for the session
   * @param {string} [params.reviewCompleteNotificationUrl] - URL for notification when review is complete
   * @param {string} [params.status] - Session status ('Scheduled', 'Closed', 'In Progress', 'Checked In', 'Processing')
   * @param {string} [params.examSid] - Exam SID
   * @param {string} [params.examLocale] - Locale for the exam
   * @param {number} [params.durationMinutes] - Duration in minutes (must be >= 1)
   * @param {string} [params.statusReason] - Reason for the status
   * @param {string} [params.recordingStartDate] - ISO date string for recording start date (with Z suffix)
   * @returns {Promise<Object>} - The updated session data
   * @throws {ParamsNotFoundError} - If required parameters are missing
   * @throws {ApiError} - If the API request fails
   */
  async patchSession(params = {}) {
    params = {
      ...params,
      ...this.sessionPathParams
    };

    if (!params.installSid || !params.examSid || !params.sessionSid) {
      throw new ParamsNotFoundError();
    }

    try {
      const pathReplacements = {
        installSid: params.installSid,
        examSid: params.examSid,
        sessionSid: params.sessionSid,
      };

      const url = this.buildPath(
        '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid',
        pathReplacements
      );

      const headers = this.buildHeaders();

      // Extract the payload fields according to the JOI schema with type validation
      const payload = {};

      // Only include fields that are provided in params with type validation
      if (params.reviewByDate && isValidISODate(params.reviewByDate)) {
        payload.reviewByDate = params.reviewByDate;
      }

      if (params.reviewByUserSid && isValidProctorUserSid(params.reviewByUserSid)) {
        payload.reviewByUserSid = params.reviewByUserSid;
      }

      if (params.reviewCompletedDate && isValidISODate(params.reviewCompletedDate)) {
        payload.reviewCompletedDate = params.reviewCompletedDate;
      }

      if (params.reviewRequired !== undefined) {
        payload.reviewRequired = Boolean(params.reviewRequired);
      }

      if (params.reviewCompleteNotificationUrl !== undefined) {
        // Allow null or empty string
        payload.reviewCompleteNotificationUrl = params.reviewCompleteNotificationUrl;
      }

      if (params.status) {
        // Validate against allowed values
        if (isValidSessionStatus(params.status)) {
          payload.status = params.status;
        }
      }

      if (params.examLocale !== undefined) {
        // Allow null or empty string
        payload.examLocale = params.examLocale;
      }

      if (params.durationMinutes !== undefined) {
        // Ensure it's a number and >= 1
        if (isValidDurationMinutes(params.durationMinutes)) {
          payload.durationMinutes = Number(params.durationMinutes);
        }
      }

      if (params.statusReason !== undefined) {
        payload.statusReason = String(params.statusReason);
      }

      if (params.recordingStartDate && isValidISODate(params.recordingStartDate)) {
        payload.recordingStartDate = params.recordingStartDate;
      }

      // Use PATCH method to update the session
      const response = await this.requester.patch(url, headers, payload);

      return response.data;
    } catch (error) {
      throw new ApiError();
    }
  };

  async stash(options) {
    try {
      const url = `/v1/stash`;

      const headers = this.buildHeaders();
      const payload = {};

      if(options?.ttl){
        payload.ttl = options.ttl;
      }

      if(options?.data){
        payload.data = options.data;
      }

      // Initialize code object in payload if needed
      if (options?.code) {
        payload.code = {};

        // Handle code length if provided
        if (options.code.length) {
          payload.code.length = options.code.length;
        }

        // Handle code type with validation
        if (options.code.type) {
          // Ensure type is one of the three valid options: 'numeric', 'alphanumeric', or 'alphabetic'
          const validTypes = ['numeric', 'alphanumeric', 'alphabetic'];
          if (validTypes.includes(options.code.type)) {
            payload.code.type = options.code.type;
          } else {
            console.warn(`Invalid code type: ${options.code.type}. Must be one of: ${validTypes.join(', ')}`);
            // Default to alphanumeric if invalid type provided
            payload.code.type = 'alpha';
          }
        }
      }


      const response = await this.requester.post(url, headers, payload);
      return response.data;
    } catch (error) {
      console.error('Error performing stash:', { error });
      throw new ApiError();
    }
  };

  async getStash(code, remove = true) {
    try {
      let url = `/v1/stash/${code}`;

      const headers = {};

      if(!remove){
        url += '?remove=false';
      }

      const response = await this.requester.get(url, headers);
      return response.data;
    } catch (error) {
      console.error('Error getting stash data:', { error });
      if(error.response && error.response.status === 400 && error.response.data.code === '1578'){
       throw error;
      }

      throw new ApiError();
    }
  };

  async performRpc(method, payload) {
    try {
      const url = `/v1/rpc`;
      const rpcPayload = {
        method,
        arguments: payload,
      };
      console.log('performing rpc', { url, rpcPayload });
      const response = await this.requester.post(url, {}, rpcPayload);
      return response.data;
    } catch (error) {
      console.error('Error performing RPC:', { error, method });
      throw new ApiError();
    }
  };

}

export default ApiClient;
